import { useCallback, useMemo, useState } from 'react';
import { ArrowRight, Check, Circle, Eye, Loader, LoaderCircle, Pencil, X } from 'lucide-react';

import type { ProposedActionPayloadT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { cn } from '@/lib/utils';
import { useCareStateDomain } from '@/entities/care-state-domain';
import {
  isPrescriptionWorkflowAction,
  PrescriptionCancelButton,
} from '@/features/prescription-cancellation';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Textarea } from '@/shared/ui/shadcn/textarea';

import type { ProposedActionVisibility } from '@/entities/proposed-actions';

import type { EnrichedProposedAction } from '../model';
import { PayloadOptionCard } from './PayloadOptionCard';
import { describeUnmetDependency } from './unmetDependencyCopy';

const META_ICON_BUTTON_CLASS =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded text-foreground hover:bg-muted/40 disabled:pointer-events-none disabled:opacity-50';

const TOOL_LABEL: Record<string, string> = {
  draft_prescription: 'Rx',
  draft_existing_lab: 'Lab',
  draft_lab_order_set: 'Lab Set',
  draft_native_lab_order_set: 'Lab Set',
  send_follow_up: 'Message',
  diagnose: 'Diagnosis',
  approve_refill: 'Refill',
  deny_refill: 'Refill',
  replace_refill: 'Refill',
  approve_rxchange: 'RxChange',
  approve_rxchange_with_changes: 'RxChange',
  deny_rxchange: 'RxChange',
};

function StatusIndicator({ visibility }: { visibility: ProposedActionVisibility }) {
  switch (visibility) {
    case 'failed':
      return <X className="size-4 text-destructive" strokeWidth={2.5} aria-label="Failed" />;
    case 'succeeded':
      return <Check className="size-4 text-emerald-600" strokeWidth={2.5} aria-label="Succeeded" />;
    case 'running':
      return (
        <Loader className="size-4 animate-spin text-primary" strokeWidth={2} aria-label="Running" />
      );
    case 'pending':
    case 'executable':
    case 'blocked':
    case 'closed':
    default:
      return <Circle className="size-4 text-primary" strokeWidth={2} aria-label="Pending" />;
  }
}

function extractFollowUpContent(item: EnrichedProposedAction): string {
  const { action } = item;
  if (action.payload.tool_name !== 'send_follow_up') {
    return '';
  }
  const input = action.payload.tool_input;
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return '';
  }
  const content = (input as { content?: unknown }).content;
  return typeof content === 'string' ? content.trim() : '';
}

interface ProposedActionChecklistRowProps {
  item: EnrichedProposedAction;
  onApprove?: (item: EnrichedProposedAction) => Promise<void> | void;
  onReject?: (item: EnrichedProposedAction, reason: string) => Promise<void> | void;
  onRevise?: (item: EnrichedProposedAction, reason: string) => Promise<void> | void;
  onExecute?: (item: EnrichedProposedAction) => void;
  onSwitchAlternative?: (externalId: string, alternativePayloadId: string) => void;
  onUpdatePayload?: (externalId: string, payload: ProposedActionPayloadT) => Promise<void> | void;
  isApproving?: boolean;
  isRejecting?: boolean;
}

export function ProposedActionChecklistRow({
  item,
  onApprove,
  onReject,
  onRevise,
  onExecute,
  onSwitchAlternative,
  onUpdatePayload,
  isApproving = false,
  isRejecting = false,
}: ProposedActionChecklistRowProps) {
  const { action, visibility } = item;
  const { patientUserId } = useCareStateDomain();
  const usesCancellation = isPrescriptionWorkflowAction(action);
  const canExecute = visibility === 'executable' && action.decision === 'Approved';
  const isTerminal = visibility === 'succeeded' || visibility === 'failed';
  const hasDependencies = item.unmetDependencies.length > 0;
  const dependencyReasons = item.unmetDependencies.map(describeUnmetDependency);
  const dependencyTooltip = hasDependencies ? dependencyReasons.join('\n') : undefined;
  const canReview = action.decision === 'Pending' && !isTerminal;
  const canReject = (canReview || canExecute) && !isTerminal;
  const hasAlternatives = action.alternatives.length > 0;
  const hasJustification = action.justification.trim().length > 0;
  const toolLabel = TOOL_LABEL[action.payload.tool_name];
  const executeLabel =
    action.payload.tool_name === 'draft_native_lab_order_set' ? 'Continue order' : 'Execute';

  const followUpMessageContent = extractFollowUpContent(item);
  const hasFollowUpContent = followUpMessageContent.length > 0;
  const hasRunError = visibility === 'failed' && !!action.run_error;
  const canEditFollowUp =
    onUpdatePayload !== undefined && action.payload.tool_name === 'send_follow_up' && canReview;
  const hasDetails =
    hasAlternatives || hasJustification || hasFollowUpContent || hasRunError || canEditFollowUp;

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [followUpDraft, setFollowUpDraft] = useState<string | null>(null);
  const [isSavingFollowUp, setIsSavingFollowUp] = useState(false);
  const isEditingFollowUp = followUpDraft !== null;

  const handleStartEditFollowUp = useCallback(() => {
    setFollowUpDraft(followUpMessageContent);
  }, [followUpMessageContent]);

  const handleDiscardFollowUp = useCallback(() => {
    setFollowUpDraft(null);
  }, []);

  const handleSaveFollowUp = useCallback(async () => {
    if (followUpDraft === null || onUpdatePayload === undefined) {
      return;
    }
    const trimmed = followUpDraft.trim();
    const nextPayload: ProposedActionPayloadT = {
      payload_id: action.payload.payload_id,
      tool_name: action.payload.tool_name,
      tool_input: { ...action.payload.tool_input, content: trimmed },
      justification: action.payload.justification,
    };
    setIsSavingFollowUp(true);
    try {
      await onUpdatePayload(action.external_id, nextPayload);
      setFollowUpDraft(null);
    } catch {
      // Upstream surfaces the toast; keep the draft so the clinician can retry.
    } finally {
      setIsSavingFollowUp(false);
    }
  }, [action.external_id, action.payload, followUpDraft, onUpdatePayload]);

  const orderedPayloadOptions = useMemo(() => {
    const alternatives = action.alternatives.slice();
    const hasPayloadInAlternatives = alternatives.some(
      (option) => option.payload_id === action.payload.payload_id
    );
    const merged = hasPayloadInAlternatives ? alternatives : [...alternatives, action.payload];
    return merged.sort((a, b) => a.payload_id.localeCompare(b.payload_id));
  }, [action.alternatives, action.payload]);

  const handleRejectSubmit = async (withRevision: boolean) => {
    const trimmedReason = rejectReason.trim();
    if (trimmedReason === '') {
      return;
    }
    if (withRevision) {
      if (!onRevise) {
        return;
      }
      await onRevise(item, trimmedReason);
    } else {
      if (!onReject) {
        return;
      }
      await onReject(item, trimmedReason);
    }
    setRejectDialogOpen(false);
    setRejectReason('');
  };

  return (
    <div>
      <div className={cn('flex w-full items-center gap-2 py-1', isTerminal && 'opacity-60')}>
        <StatusIndicator visibility={visibility} />

        <span
          className={cn(
            'min-w-0 flex-1 truncate text-sm font-medium',
            isTerminal
              ? 'text-muted-foreground line-through decoration-muted-foreground/30'
              : 'text-foreground'
          )}
        >
          {action.title}
        </span>

        <div className="flex shrink-0 items-center gap-1">
          {hasDetails ? (
            <Button
              type="button"
              size="chip"
              radius="pill"
              variant="ghost"
              className="text-foreground hover:bg-muted/40"
              onClick={() => {
                setReviewDialogOpen(true);
              }}
              aria-label={`Review ${action.title}`}
            >
              <Eye className="size-3" aria-hidden="true" />
              Review
            </Button>
          ) : null}

          {usesCancellation && visibility !== 'succeeded' && action.todo_status === 'Open' ? (
            <PrescriptionCancelButton
              patientUserId={patientUserId}
              target={{ proposedActionExternalId: action.external_id }}
              disabled={isApproving || isRejecting || visibility === 'running'}
            />
          ) : null}
          {!usesCancellation && !isApproving && canReject && onReject ? (
            <Button
              type="button"
              size="chip"
              radius="pill"
              className="bg-red-900 text-white hover:bg-red-900/90"
              onClick={() => {
                setRejectDialogOpen(true);
              }}
              disabled={isApproving || isRejecting}
              aria-label={`Reject ${action.title}`}
            >
              {isRejecting ? 'Rejecting\u2026' : 'Reject'}
            </Button>
          ) : null}

          {isApproving ? (
            <Button
              type="button"
              size="chip"
              radius="pill"
              className="bg-unread-badge text-white"
              disabled
              aria-label={`Approving ${action.title}`}
            >
              <LoaderCircle className="size-3 animate-spin" aria-hidden="true" />
              Approving&hellip;
            </Button>
          ) : canReview && onApprove ? (
            <Button
              type="button"
              size="chip"
              radius="pill"
              className={cn(
                'bg-unread-badge text-white hover:bg-unread-badge/90',
                hasDependencies && 'cursor-not-allowed opacity-40 hover:bg-unread-badge'
              )}
              onClick={() => {
                onApprove(item);
              }}
              disabled={isRejecting || hasDependencies}
              aria-label={`Approve ${action.title}`}
              title={dependencyTooltip}
            >
              Approve
            </Button>
          ) : null}

          {!isApproving && canExecute && onExecute ? (
            <Button
              type="button"
              size="chip"
              radius="pill"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => {
                onExecute(item);
              }}
              aria-label={`${executeLabel} ${action.title}`}
            >
              <ArrowRight className="size-3" aria-hidden="true" />
              {executeLabel}
            </Button>
          ) : null}
        </div>
      </div>

      {hasDependencies && !isTerminal ? (
        <ul aria-label={`Why ${action.title} is blocked`} className="space-y-0.5 pb-1 pl-6">
          {dependencyReasons.map((reason, index) => (
            <li key={index} className="text-xs text-muted-foreground">
              {reason}
            </li>
          ))}
        </ul>
      ) : null}

      <Dialog
        open={reviewDialogOpen}
        onOpenChange={(open) => {
          setReviewDialogOpen(open);
          if (!open) {
            setFollowUpDraft(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-lg gap-4 overflow-y-auto p-5">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">{action.title}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Review action details
              {toolLabel ? ` \u2014 ${toolLabel}` : ''}.
            </DialogDescription>
          </DialogHeader>

          {hasJustification ? (
            <section aria-label="Justification" className="space-y-1">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Justification
              </h3>
              <p className="text-sm leading-relaxed text-foreground">{action.justification}</p>
            </section>
          ) : null}

          {hasFollowUpContent || canEditFollowUp ? (
            <section aria-label="Follow-up message" className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Follow-up message
                </h3>
                {canEditFollowUp ? (
                  isEditingFollowUp ? (
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        aria-label="Discard follow-up edits"
                        className={META_ICON_BUTTON_CLASS}
                        onClick={handleDiscardFollowUp}
                        disabled={isSavingFollowUp}
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label="Save follow-up message"
                        className={META_ICON_BUTTON_CLASS}
                        onClick={() => {
                          void handleSaveFollowUp();
                        }}
                        disabled={isSavingFollowUp}
                      >
                        {isSavingFollowUp ? (
                          <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
                        ) : (
                          <Check className="h-4 w-4" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      aria-label="Edit follow-up message"
                      className={META_ICON_BUTTON_CLASS}
                      onClick={handleStartEditFollowUp}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )
                ) : null}
              </div>
              {isEditingFollowUp ? (
                <Textarea
                  value={followUpDraft ?? ''}
                  onChange={(e) => {
                    setFollowUpDraft(e.currentTarget.value);
                  }}
                  className="min-h-32 text-sm"
                  autoFocus
                  disabled={isSavingFollowUp}
                />
              ) : hasFollowUpContent ? (
                <p className="whitespace-pre-wrap break-words rounded-md bg-muted/40 px-3 py-2 text-sm leading-relaxed text-foreground">
                  {followUpMessageContent}
                </p>
              ) : (
                <p className="text-sm italic text-muted-foreground">No message yet.</p>
              )}
            </section>
          ) : null}

          {hasAlternatives ? (
            <section aria-label="Payload options" className="space-y-1">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Options
              </h3>
              <div className="overflow-x-auto pb-2">
                <div className="flex w-max items-start gap-2.5">
                  {orderedPayloadOptions.map((option) => (
                    <PayloadOptionCard
                      key={option.payload_id}
                      payload={option}
                      isSelected={option.payload_id === action.payload.payload_id}
                      onSelect={
                        onSwitchAlternative && option.payload_id !== action.payload.payload_id
                          ? () => onSwitchAlternative(action.external_id, option.payload_id)
                          : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {hasRunError ? (
            <section aria-label="Run error" className="space-y-1">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-destructive/80">
                Error
              </h3>
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm leading-relaxed text-destructive">
                {action.run_error}
              </p>
            </section>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) {
            setRejectReason('');
          }
        }}
      >
        <DialogContent className="max-w-md gap-4 p-5">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">Reject action</DialogTitle>
            <DialogDescription className="text-xs leading-relaxed text-muted-foreground">
              Share the reason for rejecting &ldquo;{action.title}&rdquo; so it is recorded.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.currentTarget.value);
            }}
            placeholder="Why are you rejecting this action?"
            className="min-h-20 text-sm"
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={isRejecting}
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isRejecting || rejectReason.trim() === ''}
              onClick={() => {
                void handleRejectSubmit(false);
              }}
            >
              {isRejecting ? 'Rejecting\u2026' : 'Reject'}
            </Button>
            {canReject && onRevise ? (
              <Button
                type="button"
                size="sm"
                className={cn(
                  'text-white',
                  hasDependencies
                    ? 'cursor-not-allowed bg-orange-600/40'
                    : 'bg-orange-600 hover:bg-orange-600/90'
                )}
                disabled={isRejecting || rejectReason.trim() === '' || hasDependencies}
                title={dependencyTooltip}
                onClick={() => {
                  void handleRejectSubmit(true);
                }}
              >
                Reject + revise
              </Button>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
